// ── HR Module Constants ─────────────────────────────────────────────────────

export const EMPLOYMENT_STATUS = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  PROBATION: "probation",
  TERMINATED: "terminated",
};

export const EMPLOYMENT_STATUS_LABELS = {
  active: "Active",
  inactive: "Inactive",
  probation: "Probation",
  terminated: "Terminated",
};

export const EMPLOYMENT_STATUS_COLORS = {
  active: "bg-emerald-100 text-emerald-700",
  inactive: "bg-slate-100 text-slate-600",
  probation: "bg-amber-100 text-amber-700",
  terminated: "bg-red-100 text-red-700",
};

export const EMPLOYMENT_TYPE = {
  FULL_TIME: "full_time",
  PART_TIME: "part_time",
  CONTRACT: "contract",
  INTERN: "intern",
};

export const EMPLOYMENT_TYPE_LABELS = {
  full_time: "Full Time",
  part_time: "Part Time",
  contract: "Contract",
  intern: "Intern",
};

export const DEPARTMENTS = [
  { value: "hr", label: "HR" },
  { value: "general", label: "General" },
  { value: "verve kitchens", label: "Verve Kitchens" },
];

/** Resolve a (read-only) department value from Verve Kitchens users into a clean display label. */
export const getDepartmentLabel = (value) => {
  if (!value) return "Unassigned";
  const known = DEPARTMENTS.find((d) => d.value.toLowerCase() === value.toLowerCase());
  if (known) return known.label;
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export const ROLES = [
  { value: "admin", label: "Admin" },
  { value: "owner", label: "Owner" },
  { value: "sales-head", label: "Sales Head" },
  { value: "sales-exec", label: "Sales Executive" },
  { value: "project-manager", label: "Project Manager" },
  { value: "finance-manager", label: "Finance Manager" },
  { value: "accountant", label: "Accountant" },
  { value: "store-manager", label: "Store Manager" },
  { value: "verve-admin", label: "Verve Admin" },
  { value: "telecaller", label: "Telecaller" },
  { value: "installation_team", label: "Installation Team" },
  { value: "hr", label: "HR" },
  { value: "account-manager", label: "Account Manager" },
];

// ── Attendance ──────────────────────────────────────────────────────────────

export const ATTENDANCE_STATUS = {
  PRESENT: "present",
  ABSENT: "absent",
  HALF_DAY: "half_day",
  LATE: "late",
  ON_LEAVE: "on_leave",
  HOLIDAY: "holiday",
  WEEK_OFF: "week_off",
};

export const ATTENDANCE_STATUS_LABELS = {
  present: "Present",
  absent: "Absent",
  half_day: "Half Day",
  late: "Late",
  on_leave: "On Leave",
  holiday: "Holiday",
  week_off: "Week Off",
};

export const ATTENDANCE_STATUS_COLORS = {
  present: "bg-emerald-100 text-emerald-700",
  absent: "bg-red-100 text-red-700",
  half_day: "bg-amber-100 text-amber-700",
  late: "bg-orange-100 text-orange-700",
  on_leave: "bg-purple-100 text-purple-700",
  holiday: "bg-blue-100 text-blue-700",
  week_off: "bg-slate-100 text-slate-500",
};

// ── Leaves ──────────────────────────────────────────────────────────────────

export const LEAVE_TYPES = [
  { value: "casual", label: "Casual Leave" },
  { value: "sick", label: "Sick Leave" },
  { value: "earned", label: "Earned Leave" },
  { value: "maternity", label: "Maternity Leave" },
  { value: "paternity", label: "Paternity Leave" },
  { value: "unpaid", label: "Unpaid Leave" },
  { value: "comp_off", label: "Comp Off" },
];

export const LEAVE_STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
  CANCELLED: "cancelled",
};

export const LEAVE_STATUS_LABELS = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  cancelled: "Cancelled",
};

export const LEAVE_STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
  cancelled: "bg-slate-100 text-slate-500",
};

export const DEFAULT_LEAVE_BALANCE = {
  casual: 12,
  sick: 10,
  earned: 15,
  comp_off: 0,
  unpaid: 0,
};

// ── Pay Setup ───────────────────────────────────────────────────────────────

export const PAY_COMPONENT_TYPES = {
  EARNING: "earning",
  DEDUCTION: "deduction",
};

export const PAY_FREQUENCIES = [
  { value: "monthly", label: "Monthly" },
  { value: "weekly", label: "Weekly" },
  { value: "daily", label: "Daily" },
];

export const DEFAULT_PAY_COMPONENTS = [
  { name: "Basic Salary", type: "earning", isFixed: true },
  { name: "HRA", type: "earning", isFixed: false },
  { name: "Special Allowance", type: "earning", isFixed: false },
  { name: "Transport Allowance", type: "earning", isFixed: false },
  { name: "PF (Employee)", type: "deduction", isFixed: false },
  { name: "ESI (Employee)", type: "deduction", isFixed: false },
  { name: "Professional Tax", type: "deduction", isFixed: false },
  { name: "TDS", type: "deduction", isFixed: false },
  { name: "Loan Deduction", type: "deduction", isFixed: false },
];

// ── Payslip ─────────────────────────────────────────────────────────────────

export const PAYSLIP_STATUS = {
  DRAFT: "draft",
  GENERATED: "generated",
  PAID: "paid",
};

export const PAYSLIP_STATUS_LABELS = {
  draft: "Draft",
  generated: "Generated",
  paid: "Paid",
};

export const PAYSLIP_STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-600",
  generated: "bg-blue-100 text-blue-700",
  paid: "bg-emerald-100 text-emerald-700",
};

export const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export const MONTH_OPTIONS = MONTHS.map((m, i) => ({
  value: String(i + 1).padStart(2, "0"),
  label: m,
}));

// ── HR Sub-nav tabs ─────────────────────────────────────────────────────────

export const HR_TABS = [
  { key: "onboarding", label: "Employee Onboarding", path: "/hr/onboarding" },
  { key: "attendance", label: "Attendance", path: "/hr/attendance" },
  { key: "leaves", label: "Leaves", path: "/hr/leaves" },
  { key: "pay-setup", label: "Pay Setup", path: "/hr/pay-setup" },
  { key: "payslips", label: "Payslips", path: "/hr/payslips" },
];
